import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, Menu, X } from "lucide-react";
import NotificationBell from "./NotificationBell";
import BrandLogo from "./BrandLogo";
import ThemeToggle from "./ThemeToggle";
import Avatar from "./ui/Avatar";

export default function Layout({ title, links = [], children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const onLogout = async () => { await logout(); nav("/", { replace: true }); };

  // Auto-close drawer on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const SidebarContent = (
    <>
      {/* Brand strip — same vertical rhythm as the main header top-strip
          so the sidebar and the page header share a baseline at every
          viewport size / zoom level. The "{title}" eyebrow appears only
          in the main header now (removed here) to avoid duplication. */}
      <div className="px-6 border-b border-rule flex items-center min-h-[68px] sm:min-h-[76px]">
        <BrandLogo size={36} testId="layout-brand" />
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            data-testid={`sidebar-${l.key}`}
          >
            {l.icon && <l.icon size={16} strokeWidth={1.7} />}
            <span>{l.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-rule">
        <div className="card card-pad">
          <div className="eyebrow">Signed in</div>
          <div className="flex items-center gap-3 mt-1">
            <Avatar src={user?.photo_url} name={user?.name} size={36} testid="sidebar-avatar" />
            <div className="min-w-0">
              <div className="font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-muted truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={onLogout} className="mt-3 btn-ghost w-full justify-center" data-testid="logout-btn">
            <LogOut size={14} /> Log out
          </button>
        </div>
        <a
          href="https://techcatalyst.world/"
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-3 text-center text-[11px] text-muted hover:text-signal transition"
          data-testid="layout-techcatalyst-credit"
        >
          A product by <span className="font-semibold" style={{ color: "var(--ink)" }}>Tech Catalyst Pvt. Ltd.</span>
        </a>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-paper">
      {/* Desktop sidebar (>= md) */}
      <aside className="w-64 shrink-0 bg-paper border-r border-rule hidden md:flex flex-col">
        {SidebarContent}
      </aside>

      {/* Mobile drawer (< md) */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="dashboard-mobile-drawer">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          />
          <aside
            className="absolute left-0 top-0 h-full w-[82%] max-w-xs bg-paper border-r border-rule flex flex-col"
            style={{ animation: "slideInFromLeft 240ms ease-out" }}
          >
            <div className="absolute top-3 right-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="dashboard-mobile-close"
                className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>
            {SidebarContent}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-rule">
          {/* min-h matches the sidebar brand strip so both sides share a
              baseline at every viewport / zoom. */}
          <div className="px-4 sm:px-6 flex items-center justify-between gap-3 min-h-[68px] sm:min-h-[76px]">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Open menu"
                data-testid="dashboard-mobile-toggle"
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-slate-100 shrink-0"
              >
                <Menu size={22} />
              </button>
              <div className="min-w-0 flex-1">
                <span className="eyebrow block truncate">{title}</span>
                <h1 className="font-display font-bold text-base sm:text-xl lg:text-2xl leading-tight truncate" data-testid="page-title">
                  {user?.name}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <ThemeToggle />
              <NotificationBell />
              <Avatar src={user?.photo_url} name={user?.name} size={32} testid="header-avatar" className="ring-2 ring-white" />
            </div>
          </div>
        </header>
        <div className="p-4 sm:p-6 md:p-8 min-w-0">{children}</div>
      </main>
    </div>
  );
}
